import {
  type AttachmentListResponse,
  AttachmentListResponseSchema,
  type AttachmentUploadResponse,
  AttachmentUploadResponseSchema,
  type GroupLookupOption,
  GroupLookupResponseSchema,
  type KeywordLookupOption,
  KeywordLookupResponseSchema,
  type ProblemDetail,
  ProblemDetailSchema,
  ShiftbookAdminFlocDeactivateRequestSchema,
  type ShiftbookAdminFlocListResponse,
  ShiftbookAdminFlocListResponseSchema,
  type ShiftbookAdminFlocMutationSummary,
  ShiftbookAdminFlocMutationSummarySchema,
  type ShiftbookAdminFlocNode,
  ShiftbookAdminFlocNodeSchema,
  type ShiftbookAdminFlocPatch,
  ShiftbookAdminFlocPatchSchema,
  type ShiftbookAdminGroup,
  type ShiftbookAdminGroupCreate,
  ShiftbookAdminGroupCreateSchema,
  ShiftbookAdminGroupMembersSchema,
  type ShiftbookAdminGroupPatch,
  ShiftbookAdminGroupPatchSchema,
  ShiftbookAdminGroupSchema,
  type ShiftbookAdminGroupsResponse,
  ShiftbookAdminGroupsResponseSchema,
  type ShiftbookAdminKeyword,
  type ShiftbookAdminKeywordCreate,
  ShiftbookAdminKeywordCreateSchema,
  type ShiftbookAdminKeywordListResponse,
  ShiftbookAdminKeywordListResponseSchema,
  type ShiftbookAdminKeywordPatch,
  ShiftbookAdminKeywordPatchSchema,
  ShiftbookAdminKeywordResponseSchema,
  type ShiftbookAdminSuggestionKapTag,
  type ShiftbookAdminSuggestionKapTagCreate,
  ShiftbookAdminSuggestionKapTagCreateSchema,
  type ShiftbookAdminSuggestionKapTagListResponse,
  ShiftbookAdminSuggestionKapTagListResponseSchema,
  type ShiftbookAdminSuggestionKapTagPatch,
  ShiftbookAdminSuggestionKapTagPatchSchema,
  ShiftbookAdminSuggestionKapTagResponseSchema,
  type ShiftbookAdminTemplate,
  type ShiftbookAdminTemplateCreate,
  ShiftbookAdminTemplateCreateSchema,
  type ShiftbookAdminTemplateListResponse,
  ShiftbookAdminTemplateListResponseSchema,
  type ShiftbookAdminTemplatePatch,
  ShiftbookAdminTemplatePatchSchema,
  ShiftbookAdminTemplateSchema,
  ShiftbookAdminUserSiteRolesPatchSchema,
  type ShiftbookAdminUserSiteRolesResponse,
  ShiftbookAdminUserSiteRolesResponseSchema,
  type ShiftbookAdminUsersResponse,
  ShiftbookAdminUsersResponseSchema,
  type ShiftbookEntryWorkspaceSaveRequest,
  ShiftbookEntryWorkspaceSaveRequestSchema,
  type ShiftbookEntryWorkspaceSaveResponse,
  ShiftbookEntryWorkspaceSaveResponseSchema,
  type ShiftbookNotificationListResponse,
  ShiftbookNotificationListResponseSchema,
  ShiftbookNotificationUnreadCountResponseSchema,
  type ShiftbookTemplateLookupResponse,
  ShiftbookTemplateLookupResponseSchema,
  SiteLookupResponseSchema,
  type SiteOption,
  type SiteRoleAssignments,
  type SuggestionKapTagLookupOption,
  SuggestionKapTagLookupResponseSchema,
  UserLookupResponseSchema,
  type UserRichOption,
} from '@blms/shared/schemas/shiftbook';
import {
  type ShiftbookEntryDetail,
  ShiftbookEntryDetailSchema,
  type ShiftbookEntryListResponse,
  ShiftbookEntryListResponseSchema,
  type ShiftbookEntryQueryParams,
} from '@blms/shared/schemas/shiftbook-entry';
import {
  buildTraceParent,
  generateRequestId,
  generateTraceId,
  REQUEST_ID_HEADER,
  TRACEPARENT_HEADER,
} from '@blms/shared/telemetry/correlation';
import { FRONTEND_API_EVENTS } from '@blms/shared/telemetry/events';
import {
  authenticatedFetch,
  getSessionEpoch,
  isAuthRequiredError,
  ObsoleteSessionError,
} from '@/features/auth/session-boundary';
import { ApiError } from '@/lib/api-error';
import { env } from '@/lib/env';
import { logger } from '@/lib/logging';

import { materializeAttachment } from './attachment-upload';

const API_PREFIX = `${env.apiBaseUrl}/shiftbook`;

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

const buildCorrelationPayload = () => {
  const traceId = generateTraceId();
  return {
    requestId: generateRequestId(),
    traceId,
    traceParent: buildTraceParent(traceId),
  };
};

const buildHeaders = (
  optionsHeaders: HeadersInit | undefined,
  correlation: ReturnType<typeof buildCorrelationPayload>,
) => {
  const headers = new Headers(optionsHeaders);
  headers.set(REQUEST_ID_HEADER, correlation.requestId);
  headers.set(TRACEPARENT_HEADER, correlation.traceParent);
  return headers;
};

const toLogPath = (url: string): string => {
  try {
    return new URL(url, env.apiBaseUrl).pathname;
  } catch {
    return url.split('?')[0];
  }
};

const fetchWithLogging = async (
  method: HttpMethod,
  url: string,
  options: Omit<RequestInit, 'method' | 'headers' | 'credentials'> & { headers?: HeadersInit } = {},
) => {
  const startedAt = performance.now();
  const correlation = buildCorrelationPayload();
  const path = toLogPath(url);

  logger.info('[api] request started', {
    event: FRONTEND_API_EVENTS.requestStarted,
    requestId: correlation.requestId,
    traceId: correlation.traceId,
    path,
    method,
  });

  const headers = buildHeaders(options.headers, correlation);

  let response: Response;
  try {
    response = await authenticatedFetch(url, {
      method,
      headers,
      credentials: 'include',
      ...options,
    });
  } catch (error) {
    if (isAuthRequiredError(error) || error instanceof ObsoleteSessionError) {
      logger.warn('[api] session changed', {
        event: FRONTEND_API_EVENTS.requestFailed,
        path,
        method,
      });
      throw error;
    }
    const durationMs = Math.max(0, performance.now() - startedAt);
    logger.error('[api] request failed', {
      event: FRONTEND_API_EVENTS.requestFailed,
      requestId: correlation.requestId,
      traceId: correlation.traceId,
      path,
      method,
      durationMs,
      status: 0,
      error,
    });
    throw error;
  }

  const durationMs = Math.max(0, performance.now() - startedAt);
  const status = response.status;
  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    logger.warn('[api] request failed', {
      event: FRONTEND_API_EVENTS.requestFailed,
      requestId: correlation.requestId,
      traceId: correlation.traceId,
      path,
      method,
      status,
      durationMs,
    });
    return { ok: false as const, response, body } as const;
  }

  logger.info('[api] request completed', {
    event: FRONTEND_API_EVENTS.requestSucceeded,
    requestId: correlation.requestId,
    traceId: correlation.traceId,
    path,
    method,
    status,
    durationMs,
  });

  return { ok: true as const, response };
};

export type LookupKind = 'sites' | 'users' | 'keywords' | 'suggestionKapTags' | 'groups';

export type LookupTypeMap = {
  sites: SiteOption;
  users: UserRichOption;
  keywords: KeywordLookupOption;
  suggestionKapTags: SuggestionKapTagLookupOption;
  groups: GroupLookupOption;
};

type LookupQueryOptions = {
  site?: string;
  flocId?: string;
  limit?: number;
  q?: string;
};

/**
 * Build URL with query params using repeat-key encoding for arrays.
 */
function buildUrl(path: string, params?: Partial<ShiftbookEntryQueryParams>): string {
  const url = new URL(path, env.apiBaseUrl);
  if (!params) return url.toString();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        url.searchParams.append(key, String(item));
      }
    } else {
      url.searchParams.set(key, String(value));
    }
  }

  return url.toString();
}

export function parseProblemDetail(body: unknown): ProblemDetail | undefined {
  const parsed = ProblemDetailSchema.safeParse(body);
  if (!parsed.success) {
    return undefined;
  }
  return parsed.data;
}

/**
 * Fetch entries list from the mock API.
 * Validates response with Zod schema — throws on invalid data.
 */
export async function fetchEntries(
  params?: Partial<ShiftbookEntryQueryParams>,
): Promise<ShiftbookEntryListResponse> {
  const url = buildUrl(`${API_PREFIX}/entries`, params);
  const result = await fetchWithLogging('GET', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookEntryListResponseSchema.parse(data);
}

function extractFilenameFromContentDisposition(headerValue: string | null): string | undefined {
  if (!headerValue) {
    return undefined;
  }

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(headerValue);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const quotedMatch = /filename="([^"]+)"/i.exec(headerValue);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const unquotedMatch = /filename=([^;]+)/i.exec(headerValue);
  if (unquotedMatch?.[1]) {
    return unquotedMatch[1].trim();
  }

  return undefined;
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  // Defer revocation so browsers can start the download reliably before URL cleanup.
  window.setTimeout(() => {
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
  }, 0);
}

/** Fetch-based download that works reliably in PWA standalone mode. */
export async function downloadEntriesXlsx(
  params?: Partial<ShiftbookEntryQueryParams>,
): Promise<void> {
  const url = buildUrl(`${API_PREFIX}/entries/export.xlsx`, params);
  const result = await fetchWithLogging('GET', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const blob = await result.response.blob();
  const filename =
    extractFilenameFromContentDisposition(result.response.headers.get('content-disposition')) ??
    `shiftbook-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
  triggerBrowserDownload(blob, filename);
}

/**
 * Fetch a single entry detail by ID.
 * Validates the dialog payload contract with shared schemas.
 */
export async function fetchEntryDetail(id: string): Promise<ShiftbookEntryDetail> {
  const url = `${API_PREFIX}/entries/${encodeURIComponent(id)}`;
  const result = await fetchWithLogging('GET', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookEntryDetailSchema.parse(data);
}

export async function deleteEntry(id: string): Promise<void> {
  const url = `${API_PREFIX}/entries/${encodeURIComponent(id)}`;
  const result = await fetchWithLogging('DELETE', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }
}

export async function fetchLookupOptions<K extends LookupKind>(
  kind: K,
  options?: LookupQueryOptions,
): Promise<LookupTypeMap[K][]> {
  const lookupPath = kind === 'suggestionKapTags' ? 'suggestion-kap-tags' : kind;
  const url = new URL(`${API_PREFIX}/lookups/${lookupPath}`);
  if (options?.site) {
    url.searchParams.set('site', options.site);
  }
  if (kind === 'keywords') {
    if (options?.flocId) {
      url.searchParams.set('flocId', options.flocId);
    }
    if (options?.limit) {
      url.searchParams.set('limit', String(options.limit));
    }
    if (options?.q) {
      url.searchParams.set('q', options.q);
    }
  } else if (kind === 'suggestionKapTags' && options?.q) {
    url.searchParams.set('q', options.q);
  }

  const result = await fetchWithLogging('GET', url.toString());
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  switch (kind) {
    case 'sites':
      return SiteLookupResponseSchema.parse(data) as LookupTypeMap[K][];
    case 'users':
      return UserLookupResponseSchema.parse(data) as LookupTypeMap[K][];
    case 'keywords':
      return KeywordLookupResponseSchema.parse(data) as LookupTypeMap[K][];
    case 'suggestionKapTags':
      return SuggestionKapTagLookupResponseSchema.parse(data) as LookupTypeMap[K][];
    case 'groups':
      return GroupLookupResponseSchema.parse(data) as LookupTypeMap[K][];
    default: {
      const _exhaustive: never = kind;
      throw new Error(`Unsupported lookup kind: ${_exhaustive}`);
    }
  }
}

export async function fetchAdminGroups(): Promise<ShiftbookAdminGroupsResponse> {
  // include_inactive: the admin page is the ONLY place deactivated groups can
  // be reactivated from — without the flag they vanish permanently after a
  // reload (server default is active-only).
  const url = `${API_PREFIX}/admin/groups?include_inactive=true`;
  const result = await fetchWithLogging('GET', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminGroupsResponseSchema.parse(data);
}

export async function createAdminGroup(
  payload: ShiftbookAdminGroupCreate,
): Promise<ShiftbookAdminGroup> {
  const url = `${API_PREFIX}/admin/groups`;
  const result = await fetchWithLogging('POST', url, {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(ShiftbookAdminGroupCreateSchema.parse(payload)),
  });
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminGroupSchema.parse(data);
}

export async function patchAdminGroup(params: {
  groupId: string;
  patch: ShiftbookAdminGroupPatch;
}): Promise<ShiftbookAdminGroup> {
  const url = `${API_PREFIX}/admin/groups/${params.groupId}`;
  const result = await fetchWithLogging('PATCH', url, {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(ShiftbookAdminGroupPatchSchema.parse(params.patch)),
  });
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminGroupSchema.parse(data);
}

export async function replaceAdminGroupMembers(params: {
  groupId: string;
  memberIds: string[];
  baselineMemberIds: string[];
}): Promise<ShiftbookAdminGroup> {
  const url = `${API_PREFIX}/admin/groups/${params.groupId}/members`;
  const result = await fetchWithLogging('PUT', url, {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(
      ShiftbookAdminGroupMembersSchema.parse({
        memberIds: params.memberIds,
        baselineMemberIds: params.baselineMemberIds,
      }),
    ),
  });
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminGroupSchema.parse(data);
}

export async function fetchAdminUsers(): Promise<ShiftbookAdminUsersResponse> {
  const url = `${API_PREFIX}/admin/users`;
  const result = await fetchWithLogging('GET', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminUsersResponseSchema.parse(data);
}

export async function updateAdminUserSiteRoles(params: {
  userId: string;
  siteRoles: SiteRoleAssignments;
}): Promise<ShiftbookAdminUserSiteRolesResponse> {
  const payload = ShiftbookAdminUserSiteRolesPatchSchema.parse({
    siteRoles: params.siteRoles,
  });
  const url = `${API_PREFIX}/admin/users/${encodeURIComponent(params.userId)}/site-roles`;
  const result = await fetchWithLogging('PATCH', url, {
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminUserSiteRolesResponseSchema.parse(data);
}

export async function fetchAdminFlocNodes(options?: {
  site?: string;
  includeInactive?: boolean;
}): Promise<ShiftbookAdminFlocListResponse> {
  const url = new URL(`${API_PREFIX}/admin/flocs`);
  const includeInactive = options?.includeInactive ?? true;
  url.searchParams.set('include_inactive', String(includeInactive));
  if (options?.site) {
    url.searchParams.set('site', options.site);
  }

  const result = await fetchWithLogging('GET', url.toString());
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminFlocListResponseSchema.parse(data);
}

export async function fetchAdminKeywords(options?: {
  site?: string;
  includeInactive?: boolean;
}): Promise<ShiftbookAdminKeywordListResponse> {
  const url = new URL(`${API_PREFIX}/admin/keywords`);
  const includeInactive = options?.includeInactive ?? true;
  url.searchParams.set('include_inactive', String(includeInactive));
  if (options?.site) {
    url.searchParams.set('site', options.site);
  }

  const result = await fetchWithLogging('GET', url.toString());
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminKeywordListResponseSchema.parse(data);
}

export async function createAdminKeyword(params: {
  request: ShiftbookAdminKeywordCreate;
}): Promise<ShiftbookAdminKeyword> {
  const payload = ShiftbookAdminKeywordCreateSchema.parse(params.request);
  const url = `${API_PREFIX}/admin/keywords`;
  const result = await fetchWithLogging('POST', url, {
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminKeywordResponseSchema.parse(data);
}

export async function patchAdminKeyword(params: {
  keywordId: string;
  patch: ShiftbookAdminKeywordPatch;
}): Promise<ShiftbookAdminKeyword> {
  const payload = ShiftbookAdminKeywordPatchSchema.parse(params.patch);
  const url = `${API_PREFIX}/admin/keywords/${encodeURIComponent(params.keywordId)}`;
  const result = await fetchWithLogging('PATCH', url, {
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminKeywordResponseSchema.parse(data);
}

export async function deactivateAdminKeyword(params: {
  keywordId: string;
}): Promise<ShiftbookAdminKeyword> {
  const url = `${API_PREFIX}/admin/keywords/${encodeURIComponent(params.keywordId)}/deactivate`;
  const result = await fetchWithLogging('POST', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminKeywordResponseSchema.parse(data);
}

export async function activateAdminKeyword(params: {
  keywordId: string;
}): Promise<ShiftbookAdminKeyword> {
  const url = `${API_PREFIX}/admin/keywords/${encodeURIComponent(params.keywordId)}/activate`;
  const result = await fetchWithLogging('POST', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminKeywordResponseSchema.parse(data);
}

export async function fetchAdminTemplates(options?: {
  site?: string;
  flocId?: string;
  includeInactive?: boolean;
}): Promise<ShiftbookAdminTemplateListResponse> {
  const url = new URL(`${API_PREFIX}/admin/templates`);
  const includeInactive = options?.includeInactive ?? true;
  url.searchParams.set('include_inactive', String(includeInactive));
  if (options?.site) {
    url.searchParams.set('site', options.site);
  }
  if (options?.flocId) {
    url.searchParams.set('floc_id', options.flocId);
  }

  const result = await fetchWithLogging('GET', url.toString());
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminTemplateListResponseSchema.parse(data);
}

export async function createAdminTemplate(params: {
  request: ShiftbookAdminTemplateCreate;
}): Promise<ShiftbookAdminTemplate> {
  const payload = ShiftbookAdminTemplateCreateSchema.parse(params.request);
  const url = `${API_PREFIX}/admin/templates`;
  const result = await fetchWithLogging('POST', url, {
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminTemplateSchema.parse(data);
}

export async function fetchAdminSuggestionKapTags(options?: {
  includeInactive?: boolean;
  site?: string;
  q?: string;
}): Promise<ShiftbookAdminSuggestionKapTagListResponse> {
  const url = new URL(`${API_PREFIX}/admin/suggestion-kap-tags`);
  const includeInactive = options?.includeInactive ?? true;
  if (options?.site) {
    url.searchParams.set('site', options.site);
  }
  url.searchParams.set('include_inactive', String(includeInactive));
  if (options?.q) {
    url.searchParams.set('q', options.q);
  }

  const result = await fetchWithLogging('GET', url.toString());
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminSuggestionKapTagListResponseSchema.parse(data);
}

export async function createAdminSuggestionKapTag(params: {
  request: ShiftbookAdminSuggestionKapTagCreate;
}): Promise<ShiftbookAdminSuggestionKapTag> {
  const payload = ShiftbookAdminSuggestionKapTagCreateSchema.parse(params.request);
  const url = `${API_PREFIX}/admin/suggestion-kap-tags`;
  const result = await fetchWithLogging('POST', url, {
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminSuggestionKapTagResponseSchema.parse(data);
}

export async function patchAdminSuggestionKapTag(params: {
  tagId: string;
  patch: ShiftbookAdminSuggestionKapTagPatch;
}): Promise<ShiftbookAdminSuggestionKapTag> {
  const payload = ShiftbookAdminSuggestionKapTagPatchSchema.parse(params.patch);
  const url = `${API_PREFIX}/admin/suggestion-kap-tags/${encodeURIComponent(params.tagId)}`;
  const result = await fetchWithLogging('PATCH', url, {
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminSuggestionKapTagResponseSchema.parse(data);
}

export async function deactivateAdminSuggestionKapTag(params: {
  tagId: string;
}): Promise<ShiftbookAdminSuggestionKapTag> {
  const url = `${API_PREFIX}/admin/suggestion-kap-tags/${encodeURIComponent(params.tagId)}/deactivate`;
  const result = await fetchWithLogging('POST', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminSuggestionKapTagResponseSchema.parse(data);
}

export async function activateAdminSuggestionKapTag(params: {
  tagId: string;
}): Promise<ShiftbookAdminSuggestionKapTag> {
  const url = `${API_PREFIX}/admin/suggestion-kap-tags/${encodeURIComponent(params.tagId)}/activate`;
  const result = await fetchWithLogging('POST', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminSuggestionKapTagResponseSchema.parse(data);
}

export async function patchAdminTemplate(params: {
  templateId: string;
  patch: ShiftbookAdminTemplatePatch;
}): Promise<ShiftbookAdminTemplate> {
  const payload = ShiftbookAdminTemplatePatchSchema.parse(params.patch);
  const url = `${API_PREFIX}/admin/templates/${encodeURIComponent(params.templateId)}`;
  const result = await fetchWithLogging('PATCH', url, {
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminTemplateSchema.parse(data);
}

export async function deactivateAdminTemplate(params: {
  templateId: string;
}): Promise<ShiftbookAdminTemplate> {
  const url = `${API_PREFIX}/admin/templates/${encodeURIComponent(params.templateId)}/deactivate`;
  const result = await fetchWithLogging('POST', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminTemplateSchema.parse(data);
}

export async function activateAdminTemplate(params: {
  templateId: string;
}): Promise<ShiftbookAdminTemplate> {
  const url = `${API_PREFIX}/admin/templates/${encodeURIComponent(params.templateId)}/activate`;
  const result = await fetchWithLogging('POST', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminTemplateSchema.parse(data);
}

export async function fetchTemplateLookupOptions(params: {
  flocId: string;
}): Promise<ShiftbookTemplateLookupResponse> {
  const url = new URL(`${API_PREFIX}/lookups/templates`);
  url.searchParams.set('floc_id', params.flocId);

  const result = await fetchWithLogging('GET', url.toString());
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookTemplateLookupResponseSchema.parse(data);
}

export async function patchAdminFlocNode(params: {
  nodeId: string;
  patch: ShiftbookAdminFlocPatch;
}): Promise<ShiftbookAdminFlocNode> {
  const payload = ShiftbookAdminFlocPatchSchema.parse(params.patch);
  const url = `${API_PREFIX}/admin/flocs/${encodeURIComponent(params.nodeId)}`;
  const result = await fetchWithLogging('PATCH', url, {
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminFlocNodeSchema.parse(data);
}

export async function deactivateAdminFlocNode(params: {
  nodeId: string;
  confirmCascade: boolean;
}): Promise<ShiftbookAdminFlocMutationSummary> {
  const payload = ShiftbookAdminFlocDeactivateRequestSchema.parse({
    confirmCascade: params.confirmCascade,
  });
  const url = `${API_PREFIX}/admin/flocs/${encodeURIComponent(params.nodeId)}/deactivate`;
  const result = await fetchWithLogging('POST', url, {
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminFlocMutationSummarySchema.parse(data);
}

export async function activateAdminFlocNode(params: {
  nodeId: string;
}): Promise<ShiftbookAdminFlocMutationSummary> {
  const url = `${API_PREFIX}/admin/flocs/${encodeURIComponent(params.nodeId)}/activate`;
  const result = await fetchWithLogging('POST', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookAdminFlocMutationSummarySchema.parse(data);
}

export async function fetchEntryAttachments(entryId: string): Promise<AttachmentListResponse> {
  const url = `${API_PREFIX}/entries/${encodeURIComponent(entryId)}/attachments`;
  const result = await fetchWithLogging('GET', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return AttachmentListResponseSchema.parse(data);
}

export async function uploadEntryAttachment(
  entryId: string,
  file: File,
): Promise<AttachmentUploadResponse> {
  const formData = new FormData();
  const epoch = getSessionEpoch();
  const blob = await materializeAttachment({ blob: file, filename: file.name });
  if (epoch !== getSessionEpoch()) throw new ObsoleteSessionError();
  formData.append('file', blob, file.name);

  const url = `${API_PREFIX}/entries/${encodeURIComponent(entryId)}/attachments`;
  const result = await fetchWithLogging('POST', url, { body: formData });
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return AttachmentUploadResponseSchema.parse(data);
}

export async function deleteAttachment(attachmentId: string): Promise<void> {
  const url = `${API_PREFIX}/attachments/${encodeURIComponent(attachmentId)}`;
  const result = await fetchWithLogging('DELETE', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }
}

export function buildAttachmentDownloadUrl(attachmentId: string): string {
  return `${API_PREFIX}/attachments/${encodeURIComponent(attachmentId)}/download`;
}

/** Fetch-based download that works reliably in PWA standalone mode. */
export async function downloadAttachment(attachmentId: string, filename: string): Promise<void> {
  const url = buildAttachmentDownloadUrl(attachmentId);
  const response = await authenticatedFetch(url, { credentials: 'include' });
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText);
  }
  const blob = await response.blob();
  triggerBrowserDownload(blob, filename);
}

export function buildAttachmentThumbnailUrl(attachmentId: string): string {
  return `${API_PREFIX}/attachments/${encodeURIComponent(attachmentId)}/thumbnail`;
}

export function buildAttachmentPreviewUrl(attachmentId: string): string {
  return `${API_PREFIX}/attachments/${encodeURIComponent(attachmentId)}/preview`;
}

export function buildAttachmentEventsUrl(entryId: string): string {
  return `${API_PREFIX}/entries/${encodeURIComponent(entryId)}/attachments/events`;
}

export function buildShiftbookNotificationsWsUrl(site: string): string {
  const browserOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const url = new URL(`${API_PREFIX}/notifications/ws`, browserOrigin);
  url.searchParams.set('site', site);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

export function buildNotificationEventsUrl(site: string): string {
  return `${API_PREFIX}/notifications/events?site=${encodeURIComponent(site)}`;
}

export async function listNotifications(
  site: string,
  params?: { limit?: number; cursor?: string },
): Promise<ShiftbookNotificationListResponse> {
  const searchParams = new URLSearchParams({ site });
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.cursor) searchParams.set('cursor', params.cursor);

  const url = `${API_PREFIX}/notifications?${searchParams.toString()}`;
  const result = await fetchWithLogging('GET', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }
  const data = await result.response.json();
  return ShiftbookNotificationListResponseSchema.parse(data);
}

export async function markNotificationRead(notificationId: string, site: string): Promise<void> {
  const url = `${API_PREFIX}/notifications/read`;
  const result = await fetchWithLogging('POST', url, {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ notificationId, site }),
  });
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }
}

export async function markAllNotificationsRead(site: string): Promise<void> {
  const url = `${API_PREFIX}/notifications/read-all`;
  const result = await fetchWithLogging('POST', url, {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ site }),
  });
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }
}

export async function getNotificationUnreadCount(site: string): Promise<{ unreadCount: number }> {
  const url = `${API_PREFIX}/notifications/unread-count?site=${encodeURIComponent(site)}`;
  const result = await fetchWithLogging('GET', url);
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }
  const data = await result.response.json();
  return ShiftbookNotificationUnreadCountResponseSchema.parse(data);
}

export async function saveEntryWorkspace(params: {
  request: ShiftbookEntryWorkspaceSaveRequest;
  files?: Array<{ clientId: string; blob: Blob; filename: string; sizeBytes?: number }>;
}): Promise<ShiftbookEntryWorkspaceSaveResponse> {
  const epoch = getSessionEpoch();
  const validated = ShiftbookEntryWorkspaceSaveRequestSchema.parse(params.request);

  const formData = new FormData();
  formData.append('payload', JSON.stringify(validated));

  for (const file of params.files ?? []) {
    const blob = await materializeAttachment(file);
    if (epoch !== getSessionEpoch()) throw new ObsoleteSessionError();
    formData.append(file.clientId, blob, file.filename);
  }

  const url = `${API_PREFIX}/entry-workspace/save`;
  const result = await fetchWithLogging('POST', url, { body: formData });
  if (!result.ok) {
    throw new ApiError(result.response.status, result.response.statusText, result.body);
  }

  const data = await result.response.json();
  return ShiftbookEntryWorkspaceSaveResponseSchema.parse(data);
}
